// src/i18n/translations.ts
// Full EN / DE translation catalogue

export type Language = 'en' | 'de';

export interface Translations {
  // ── Navbar ──────────────────────────────────────────
  nav: {
    home: string;
    about: string;
    contact: string;
    getInTouch: string;
  };

  // ── Footer ──────────────────────────────────────────
  footer: {
    tagline: string;
    navigation: string;
    services: string;
    contact: string;
    allRightsReserved: string;
    serviceList: string[];
  };

  // ── Home ────────────────────────────────────────────
  home: {
    heroBadge: string;
    heroHeadline1: string;
    heroHeadlineAccent: string;
    heroHeadline2: string;
    heroSub: string;
    heroStartProject: string;
    heroLearnMore: string;
    heroStatService: string;
    heroStatCoverage: string;
    heroStatInsights: string;

    whoLabel: string;
    whoTitle1: string;
    whoTitleAccent: string;
    whoBody1: string;
    whoBody2: string;
    whoCardSub: string;
    whoBtn: string;

    servicesLabel: string;
    servicesTitle1: string;
    servicesTitleAccent: string;
    servicesSub: string;
    services: { icon: string; title: string; desc: string }[];

    audienceLabel: string;
    audienceTitle1: string;
    audienceTitleAccent: string;
    audienceSub: string;
    audiences: { icon: string; title: string; desc: string }[];

    gmedixLabel: string;
    gmedixBody: string;
    gmedixPhase1: string;
    gmedixPhase2: string;
    gmedixBtn: string;

    impactLabel: string;
    impactTitle1: string;
    impactTitleAccent: string;
    impactCard1Title: string;
    impactCard1Body: string;
    impactCard2Title: string;
    impactCard2Body: string;
    impactCard3Title: string;
    impactCard3Body: string;

    ctaTitle: string;
    ctaBody: string;
    ctaBtn: string;
  };

  // ── About ───────────────────────────────────────────
  about: {
    pageLabel: string;
    heroHeadline: string;
    heroHeadlineAccent: string;
    heroSub1: string;
    heroSub2: string;

    storyLabel: string;
    storyTitle: string;
    storyTitleAccent: string;
    storyBody1: string;
    storyBody2: string;
    storyBody3: string;
    missionTitle: string;
    missionBody: string;
    visionTitle: string;
    visionBody: string;

    approachLabel: string;
    approachTitle: string;
    approachSub: string;
    approaches: { icon: string; title: string; desc: string }[];

    expertiseLabel: string;
    expertiseTitle: string;
    expertiseTitleAccent: string;
    expertiseSub: string;
    expertise: { icon: string; title: string; desc: string }[];

    gmedixLabel: string;
    gmedixTitle: string;
    gmedixBody1: string;
    gmedixBody2: string;
    gmedixBody3: string;
    gmedixPhasesTitle: string;
    gmedixPhase1Label: string;
    gmedixPhase1Title: string;
    gmedixPhase1Body: string;
    gmedixPhase2Label: string;
    gmedixPhase2Title: string;
    gmedixPhase2Body: string;

    impactLabel: string;
    impactTitle: string;
    impactTitleAccent: string;
    impactCommitmentTitle: string;
    impactCommitmentBody: string;
    impactInitTitle: string;
    impactInitBody: string;
    impactPartnerTitle: string;
    impactPartnerBody: string;
    impactQuote: string;

    ctaTitle: string;
    ctaBody: string;
    ctaBtn: string;
  };

  // ── Contact ─────────────────────────────────────────
  contact: {
    pageLabel: string;
    heroTitle: string;
    heroSub: string;

    infoTitle: string;
    infoGeneral: string;
    infoPhone: string;
    infoAddress: string;
    infoAddressLines: string[];
    infoHoursTitle: string;
    infoHoursValue: string;
    infoImpactTitle: string;

    formTitle: string;
    formName: string;
    formNamePlaceholder: string;
    formEmail: string;
    formEmailPlaceholder: string;
    formPhone: string;
    formPhonePlaceholder: string;
    formSubject: string;
    formSubjectPlaceholder: string;
    formMessage: string;
    formMessagePlaceholder: string;
    formSend: string;
    formSending: string;

    errName: string;
    errEmailRequired: string;
    errEmailInvalid: string;
    errSubject: string;
    errMessage: string;

    successTitle: string;
    successBody: string;
    successBtn: string;

    mapTitle: string;
  };

  // ── Impressum ────────────────────────────────────────
  impressum: {
    pageLabel: string;
    heroTitle: string;
    contactInfoTitle: string;
    registerTitle: string;
    register: string;
    municipality: string;
    responsibleTitle: string;
    responsibleBody: string;
    liabilityContentTitle: string;
    liabilityContentBody: string;
    liabilityLinksTitle: string;
    liabilityLinksBody: string;
    copyrightTitle: string;
    copyrightBody: string;
  };

  // ── Theme / Lang toggle ──────────────────────────────
  ui: {
    lightMode: string;
    darkMode: string;
    systemMode: string;
    theme: string;
    language: string;
  };
}

// ─────────────────────────────────────────────────────────
// ENGLISH
// ─────────────────────────────────────────────────────────
export const en: Translations = {
  nav: {
    home: 'Home',
    about: 'About Us',
    contact: 'Contact',
    getInTouch: 'Get in Touch',
  },

  footer: {
    tagline:
      'AI-powered pharmaceutical and medical innovation—from project design and implementation to clinical application.',
    navigation: 'Navigation',
    services: 'Services',
    contact: 'Contact',
    allRightsReserved: 'All rights reserved.',
    serviceList: [
      'Drug Delivery Systems',
      'Pharmaceutical Manufacturing',
      'Smart Medical Devices',
      'Clinical Research Support',
      'Bioinformatics',
    ],
  },

  home: {
    heroBadge: 'AI-Powered Healthcare Innovation',
    heroHeadline1: 'MahMedix – ',
    heroHeadlineAccent: 'Advancing',
    heroHeadline2: 'Medicine and Pharma Projects.',
    heroSub:
      'AI-powered pharmaceutical and medical innovation—\nfrom project design, implementation, development to clinical application.',
    heroStartProject: 'Start Your Project',
    heroLearnMore: 'Learn More',
    heroStatService: 'Service Areas',
    heroStatCoverage: 'Project Coverage',
    heroStatInsights: 'Driven Insights',

    whoLabel: 'Who We Are',
    whoTitle1: 'Supporting the Future of',
    whoTitleAccent: 'Medicine and Pharmaceuticals',
    whoBody1:
      'MahMedix is dedicated to advancing the pharmaceutical and healthcare sectors through innovative, technology-driven solutions. We work alongside pharmaceutical companies, hospitals, clinics, research institutes, and universities to optimize projects, enhance decision-making, and drive meaningful outcomes.',
    whoBody2:
      'By integrating cutting-edge computational tools with deep industry expertise, we position ourselves as a reliable partner you can trust—from early concept through to clinical application.',
    whoCardSub: 'Pharmaceutical · Medical · Healthcare',
    whoBtn: 'Our Story & Mission',

    servicesLabel: 'What We Do',
    servicesTitle1: 'Comprehensive Support Across the Pharmaceutical,',
    servicesTitleAccent: 'Medicine and Healthcare Spectrum',
    servicesSub:
      'Our expertise covers the full scope of pharmaceutical development, medical innovation, and clinical practice.',
    services: [
      { icon: '💊', title: 'Drug Delivery Systems', desc: 'Developing and optimizing formulations for improved bioavailability and patient outcomes.' },
      { icon: '🏭', title: 'Pharmaceutical Manufacturing & Facility Planning', desc: 'Strategic planning for the design and implementation of production facilities for APIs and finished products in various dosage forms.' },
      { icon: '🩺', title: 'Smart Medical & Clinical Devices', desc: 'Supporting the development and integration of intelligent devices for diagnostics, monitoring, and treatment applications.' },
      { icon: '📊', title: 'Clinical & Business Strategy', desc: 'Risk assessment, program evaluation, profit estimation, and preparation of drug baskets and formularies.' },
      { icon: '🔬', title: 'Medical & Biomedical Analysis', desc: 'In-depth analysis of medical data and biomedical research to extract actionable insights.' },
      { icon: '🧪', title: 'Clinical Research Support (CRO)', desc: 'Enhancing clinical trial efficiency through data modelling and patient recruitment strategies.' },
      { icon: '🧬', title: 'Bioinformatics', desc: 'Advanced analysis of biological data, including genomics and proteomics, to support research and development.' },
    ],

    audienceLabel: 'Who We Serve',
    audienceTitle1: 'Serving the Entire',
    audienceTitleAccent: 'Healthcare Ecosystem',
    audienceSub:
      'Whether you are a multinational pharmaceutical corporation, a research institution, a healthcare provider, or an individual seeking a structured health program, our solutions adapt to your specific needs.',
    audiences: [
      { icon: '🏢', title: 'Pharma & Biotech Companies', desc: 'R&D support, manufacturing optimization, and market strategy.' },
      { icon: '🏥', title: 'Hospitals & Clinics', desc: 'Operational efficiency, patient program management, diagnostic support, and smart device integration.' },
      { icon: '🎓', title: 'Research Institutes & Universities', desc: 'Bioinformatics, biomedical analysis, and clinical research support for academic and scientific advancement.' },
      { icon: '🤖', title: 'Medical Device Developers', desc: 'Strategic support for the design, development, and clinical implementation of smart medical technologies.' },
      { icon: '👤', title: 'Health Programs & Individuals', desc: 'Data-informed health tracking and personalized wellness initiatives.' },
      { icon: '⚖️', title: 'Regulatory & Risk Management', desc: 'Proactive risk evaluation and program assessment to support compliance and safety.' },
    ],

    gmedixLabel: 'Current Project',
    gmedixBody:
      'G-Medix is a highly innovative project focused on developing a novel method and device for vascular health. Combining advanced biomedical engineering with intelligent data processing to deliver precision vascular care.',
    gmedixPhase1:
      'Hospitals, clinics, and healthcare programs—precision vascular assessment and treatment planning.',
    gmedixPhase2:
      'Medical device and companion application for personal vascular health management.',
    gmedixBtn: 'Discover G-Medix',

    impactLabel: 'Social Impact',
    impactTitle1: 'Advancing Health Equity.',
    impactTitleAccent: 'Supporting Communities in Need.',
    impactCard1Title: 'Our Commitment',
    impactCard1Body:
      'At MahMedix, we believe that access to quality healthcare and pharmaceutical innovation should not be a privilege—it should be a right. Beyond our commercial projects, we are committed to using our expertise to support vulnerable communities and address global health challenges.',
    impactCard2Title: 'Our Initiatives',
    impactCard2Body:
      'We support programs addressing childhood hunger and malnutrition. Through collaboration with local organizations, we contribute to initiatives that provide nutritious food, health screenings, vaccine and medicinal productions, and education to children in need—because healthy children build healthy futures.',
    impactCard3Title: 'Our Vision',
    impactCard3Body:
      'Every successful project brings us closer to our goal: advancing medicine and pharma—for everyone, everywhere.',

    ctaTitle: 'Ready to Elevate Your Next Pharma, Medicine or Healthcare Project?',
    ctaBody:
      "Let's discuss how MahMedix can support you in designing, implementing, and evaluating your initiatives with innovation and reliability.",
    ctaBtn: 'Contact Our Team',
  },

  about: {
    pageLabel: 'About Us',
    heroHeadline:
      'At MahMedix, we turn complex pharmaceutical, medical and healthcare challenges into',
    heroHeadlineAccent: 'successful projects.',
    heroSub1: 'Your trusted partner from concept to clinical application.',
    heroSub2:
      'MahMedix brings together a team of highly experienced professionals, combining deep industry knowledge in pharmaceuticals, medicine, healthcare, and artificial intelligence to deliver exceptional results.',

    storyLabel: 'Our Story & Mission',
    storyTitle: 'Bridging Innovation',
    storyTitleAccent: 'and Application',
    storyBody1:
      'MahMedix was founded with a clear vision: to support the pharmaceutical, medical and healthcare industries with intelligent, results-driven solutions. We believe that successful projects require more than just technical expertise—they require a deep understanding of both scientific principles and real-world clinical needs.',
    storyBody2:
      'Today, we work at the intersection of pharmaceutical science, medical technology, and data-driven innovation. Our mission is to help our partners navigate complexity, reduce risk, and deliver meaningful outcomes—from drug development and manufacturing to smart medical devices and clinical programs.',
    storyBody3:
      'In addition, we are developing our own innovative projects and preparing them for the market.',
    missionTitle: 'Our Mission',
    missionBody:
      'Help partners navigate complexity, reduce risk, and deliver meaningful outcomes across the pharmaceutical, medical, and healthcare spectrum.',
    visionTitle: 'Our Vision',
    visionBody:
      'To be the most trusted innovation partner in pharmaceutical and healthcare project development—globally.',

    approachLabel: 'Our Approach',
    approachTitle: 'How We Work',
    approachSub:
      "We don't believe in one-size-fits-all solutions. Every project is unique, and we tailor our approach accordingly.",
    approaches: [
      { icon: '🤝', title: 'Collaborative Partnership', desc: 'We work closely with your team to understand your specific goals, challenges, and constraints.' },
      { icon: '🔭', title: 'Scientific Rigor', desc: 'Our work is grounded in sound pharmaceutical and medical principles, ensuring that every recommendation is both innovative and practical.' },
      { icon: '🔄', title: 'End-to-End Support', desc: 'From initial concept and design through implementation and clinical application, we are with you at every stage.' },
      { icon: '💡', title: 'Data-Enabled Insights', desc: "We leverage advanced computational tools to uncover insights, optimize processes, and support decision-making—always with the focus on project's success." },
    ],

    expertiseLabel: 'Our Expertise',
    expertiseTitle: 'What We Bring to',
    expertiseTitleAccent: 'Your Project',
    expertiseSub:
      'Our team combines deep domain knowledge with practical experience across the pharmaceutical, medical and healthcare spectrum.',
    expertise: [
      { icon: '💊', title: 'Pharmaceutical Development', desc: 'Drug delivery systems, formulation optimization, and API manufacturing.' },
      { icon: '🏭', title: 'Facility Planning & Implementation', desc: 'Design and execution of production facilities for various dosage forms.' },
      { icon: '🩺', title: 'Smart Medical & Clinical Devices', desc: 'Development and integration of intelligent technologies for diagnostics, monitoring, and treatment.' },
      { icon: '📋', title: 'Clinical Strategy & Research', desc: 'Program evaluation, risk management, CRO support, and clinical trial optimization.' },
      { icon: '🧬', title: 'Biomedical & Bioinformatics Analysis', desc: 'Advanced analysis of medical and biological data to support research and development.' },
      { icon: '📈', title: 'Business & Market Strategy', desc: 'Profit estimation, drug basket preparation, and market access planning.' },
    ],

    gmedixLabel: 'Current Project',
    gmedixTitle: 'G-Medix',
    gmedixBody1:
      'G-Medix is a highly innovative project focused on developing a novel method and device for vascular health. Designed to address critical needs in diagnostics, monitoring, and intervention, G-Medix combines advanced biomedical engineering with intelligent data processing to deliver precision vascular care.',
    gmedixBody2:
      'The project is being developed for multiple applications across the healthcare spectrum. In its initial phase, G-Medix will serve hospitals, clinics, and healthcare programs—providing professionals with a powerful tool for vascular assessment and treatment planning. In later phases, the technology will expand to include a medical device and companion application for individuals.',
    gmedixBody3:
      'By bridging the gap between clinical innovation and personal health technology, G-Medix aims to improve early detection, enhance patient outcomes, and empower individuals to take control of their vascular health—starting in professional care and extending into everyday life.',
    gmedixPhasesTitle: 'Development Phases',
    gmedixPhase1Label: 'Phase 1',
    gmedixPhase1Title: 'Clinical & Professional',
    gmedixPhase1Body:
      'Hospitals, clinics, and healthcare programs with precision vascular assessment and treatment planning tools.',
    gmedixPhase2Label: 'Phase 2',
    gmedixPhase2Title: 'Personal Health',
    gmedixPhase2Body:
      'Medical device and companion application enabling personal vascular health management beyond clinical settings.',

    impactLabel: 'Social Impact',
    impactTitle: 'Advancing Health Equity.',
    impactTitleAccent: 'Supporting Communities in Need.',
    impactCommitmentTitle: 'Our Commitment',
    impactCommitmentBody:
      'At MahMedix, we believe that access to quality healthcare and pharmaceutical innovation should not be a privilege—it should be a right. Beyond our commercial projects, we are committed to using our expertise to support vulnerable communities and address global health challenges.',
    impactInitTitle: 'Our Initiatives',
    impactInitBody:
      'We support programs addressing childhood hunger and malnutrition. Through collaboration with local organizations and companies we contribute to initiatives that provide nutritious food, health screenings, vaccine and medicinal productions, and education to children in need—because healthy children build healthy futures.',
    impactPartnerTitle: 'Partner With Us',
    impactPartnerBody:
      'If you represent an organization working in global health, humanitarian aid, or community development, we would love to hear from you.',
    impactQuote:
      'Every successful project brings us closer to our goal: advancing medicine and pharma—for everyone, everywhere.',

    ctaTitle: 'Ready to Partner with MahMedix?',
    ctaBody:
      "Let's talk about how we can support your next pharmaceutical, medical, or healthcare initiative.",
    ctaBtn: 'Contact Our Team',
  },

  contact: {
    pageLabel: 'Contact Us',
    heroTitle: "Let's Connect.",
    heroSub: "Have a project in mind or want to learn more about our innovations? We're here to help.",

    infoTitle: 'Contact Information',
    infoGeneral: 'General Inquiries',
    infoPhone: 'Phone',
    infoAddress: 'Office Address',
    infoAddressLines: ['Ernst-Thälmann Ring 64a', '17491 Greifswald', 'Germany'],
    infoHoursTitle: 'Office Hours',
    infoHoursValue: 'Monday – Friday: 9:00 – 18:00',
    infoImpactTitle: 'Social Impact Inquiries',

    formTitle: 'Get in Touch',
    formName: 'Name',
    formNamePlaceholder: 'Your full name',
    formEmail: 'Email',
    formEmailPlaceholder: 'your@email.com',
    formPhone: 'Phone',
    formPhonePlaceholder: '+49 xxx xxxxxxxx',
    formSubject: 'Subject',
    formSubjectPlaceholder: 'How can we help?',
    formMessage: 'Message',
    formMessagePlaceholder: 'Tell us about your project or inquiry…',
    formSend: 'Send Message',
    formSending: 'Sending…',

    errName: 'Name is required.',
    errEmailRequired: 'Email is required.',
    errEmailInvalid: 'Please enter a valid email address.',
    errSubject: 'Subject is required.',
    errMessage: 'Message is required.',

    successTitle: 'Message Sent!',
    successBody: "Thank you for reaching out. We'll get back to you within one business day.",
    successBtn: 'Send Another Message',

    mapTitle: 'Find Us',
  },

  impressum: {
    pageLabel: 'Legal',
    heroTitle: 'Impressum',
    contactInfoTitle: 'Contact Information',
    registerTitle: 'Register Information',
    register: 'Register',
    municipality: 'Municipality code',
    responsibleTitle: 'Responsible for Content According to § 55 Abs. 2 RStV',
    responsibleBody: 'MahMedix',
    liabilityContentTitle: 'Liability for Content',
    liabilityContentBody:
      'As service providers, we are responsible for our own content on these pages. However, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information according to general laws remain unaffected. However, any liability in this regard is only possible from the moment of knowledge of a specific legal infringement. Upon notification of such violations, we will remove this content immediately.',
    liabilityLinksTitle: 'Liability for Links',
    liabilityLinksBody:
      'Our website contains links to external websites operated by third parties, over whose content we have no influence. Therefore, we cannot assume any liability for this external content. The respective provider or operator of the linked pages is always responsible for the content. At the time of linking, the linked pages were checked for possible legal violations. No illegal content was discernible at the time of linking. Permanent monitoring of the content of linked pages is not reasonable without concrete indications of a legal violation. If we become aware of legal violations, we will remove such links immediately.',
    copyrightTitle: 'Copyright',
    copyrightBody:
      'The content and works created by the site operator on these pages are subject to German copyright law. Duplication, processing, distribution, or any form of commercialization of such material beyond the scope of copyright law requires the prior written consent of the respective author or creator. Downloads and copies of this website are only permitted for private, non-commercial use. Insofar as the content on this site was not created by the operator, the copyrights of third parties are respected. Content from third parties is marked as such. Should you become aware of a copyright infringement, please notify us accordingly. Upon notification of violations, we will remove such content immediately.',
  },

  ui: {
    lightMode: 'Light',
    darkMode: 'Dark',
    systemMode: 'System',
    theme: 'Theme',
    language: 'Language',
  },
};

// ─────────────────────────────────────────────────────────
// GERMAN
// ─────────────────────────────────────────────────────────
export const de: Translations = {
  nav: {
    home: 'Startseite',
    about: 'Über uns',
    contact: 'Kontakt',
    getInTouch: 'Kontakt aufnehmen',
  },

  footer: {
    tagline:
      'KI-gestützte pharmazeutische und medizinische Innovation – vom Projektdesign und der Implementierung bis zur klinischen Anwendung.',
    navigation: 'Navigation',
    services: 'Leistungen',
    contact: 'Kontakt',
    allRightsReserved: 'Alle Rechte vorbehalten.',
    serviceList: [
      'Arzneimittelabgabesysteme',
      'Pharmazeutische Herstellung',
      'Intelligente Medizingeräte',
      'Klinische Forschungsunterstützung',
      'Bioinformatik',
    ],
  },

  home: {
    heroBadge: 'KI-gestützte Gesundheitsinnovation',
    heroHeadline1: 'MahMedix – ',
    heroHeadlineAccent: 'Wegweisend',
    heroHeadline2: 'in Medizin und Pharma.',
    heroSub:
      'KI-gestützte pharmazeutische und medizinische Innovation –\nvom Projektdesign über Implementierung und Entwicklung bis zur klinischen Anwendung.',
    heroStartProject: 'Projekt starten',
    heroLearnMore: 'Mehr erfahren',
    heroStatService: 'Leistungsbereiche',
    heroStatCoverage: 'Projektabdeckung',
    heroStatInsights: 'Datengestützte Insights',

    whoLabel: 'Wer wir sind',
    whoTitle1: 'Die Zukunft der',
    whoTitleAccent: 'Medizin und Pharmazie fördern',
    whoBody1:
      'MahMedix widmet sich der Weiterentwicklung der pharmazeutischen und Gesundheitssektoren durch innovative, technologiegetriebene Lösungen. Wir arbeiten mit Pharmaunternehmen, Krankenhäusern, Kliniken, Forschungsinstituten und Universitäten zusammen, um Projekte zu optimieren, Entscheidungsprozesse zu verbessern und bedeutungsvolle Ergebnisse zu erzielen.',
    whoBody2:
      'Durch die Integration modernster Berechnungstools mit tiefem Branchen-Know-how positionieren wir uns als verlässlicher Partner – von der ersten Konzeptidee bis zur klinischen Anwendung.',
    whoCardSub: 'Pharma · Medizin · Gesundheitswesen',
    whoBtn: 'Unsere Geschichte & Mission',

    servicesLabel: 'Was wir tun',
    servicesTitle1: 'Umfassende Unterstützung im Bereich Pharma,',
    servicesTitleAccent: 'Medizin und Gesundheitswesen',
    servicesSub:
      'Unsere Expertise umfasst das gesamte Spektrum der pharmazeutischen Entwicklung, medizinischen Innovation und klinischen Praxis.',
    services: [
      { icon: '💊', title: 'Arzneimittelabgabesysteme', desc: 'Entwicklung und Optimierung von Formulierungen zur Verbesserung der Bioverfügbarkeit und Patientenergebnisse.' },
      { icon: '🏭', title: 'Pharmazeutische Herstellung & Anlagenplanung', desc: 'Strategische Planung für Entwurf und Implementierung von Produktionsanlagen für APIs und Fertigprodukte in verschiedenen Darreichungsformen.' },
      { icon: '🩺', title: 'Intelligente Medizin- & Klinische Geräte', desc: 'Unterstützung bei der Entwicklung und Integration intelligenter Geräte für Diagnostik, Überwachung und Behandlungsanwendungen.' },
      { icon: '📊', title: 'Klinische & Geschäftsstrategie', desc: 'Risikoabschätzung, Programmbewertung, Gewinnschätzung sowie Erstellung von Arzneimittelkörben und Formularen.' },
      { icon: '🔬', title: 'Medizinische & Biomedizinische Analyse', desc: 'Eingehende Analyse medizinischer Daten und biomedizinischer Forschung zur Gewinnung verwertbarer Erkenntnisse.' },
      { icon: '🧪', title: 'Klinische Forschungsunterstützung (CRO)', desc: 'Steigerung der Effizienz klinischer Studien durch Datenmodellierung und Patientenrekrutierungsstrategien.' },
      { icon: '🧬', title: 'Bioinformatik', desc: 'Fortschrittliche Analyse biologischer Daten, einschließlich Genomik und Proteomik, zur Unterstützung von Forschung und Entwicklung.' },
    ],

    audienceLabel: 'Wer wir bedienen',
    audienceTitle1: 'Das gesamte',
    audienceTitleAccent: 'Gesundheitsökosystem bedienen',
    audienceSub:
      'Ob multinationaler Pharmakozern, Forschungseinrichtung, Gesundheitsdienstleister oder Einzelperson – unsere Lösungen passen sich Ihren spezifischen Bedürfnissen an.',
    audiences: [
      { icon: '🏢', title: 'Pharma- & Biotechnologieunternehmen', desc: 'F&E-Unterstützung, Fertigungsoptimierung und Marktstrategie.' },
      { icon: '🏥', title: 'Krankenhäuser & Kliniken', desc: 'Betriebseffizienz, Patientenprogrammmanagement, Diagnoseunterstützung und Integration intelligenter Geräte.' },
      { icon: '🎓', title: 'Forschungsinstitute & Universitäten', desc: 'Bioinformatik, biomedizinische Analyse und klinische Forschungsunterstützung für den akademischen und wissenschaftlichen Fortschritt.' },
      { icon: '🤖', title: 'Medizingerätehersteller', desc: 'Strategische Unterstützung für Design, Entwicklung und klinische Implementierung intelligenter Medizintechnologien.' },
      { icon: '👤', title: 'Gesundheitsprogramme & Einzelpersonen', desc: 'Datengestütztes Gesundheitsmonitoring und personalisierte Wellnessinitiativen.' },
      { icon: '⚖️', title: 'Regulierung & Risikomanagement', desc: 'Proaktive Risikobewertung und Programmbewertung zur Unterstützung von Compliance und Sicherheit.' },
    ],

    gmedixLabel: 'Aktuelles Projekt',
    gmedixBody:
      'G-Medix ist ein hochinnovatives Projekt zur Entwicklung einer neuartigen Methode und eines Geräts für die Gefäßgesundheit. Die Verbindung fortschrittlicher biomedizinischer Technik mit intelligenter Datenverarbeitung für präzise Gefäßversorgung.',
    gmedixPhase1:
      'Krankenhäuser, Kliniken und Gesundheitsprogramme – präzise Gefäßbeurteilung und Behandlungsplanung.',
    gmedixPhase2:
      'Medizingerät und Begleit-App für das persönliche Gefäßgesundheitsmanagement.',
    gmedixBtn: 'G-Medix entdecken',

    impactLabel: 'Gesellschaftliche Wirkung',
    impactTitle1: 'Gesundheitsgerechtigkeit fördern.',
    impactTitleAccent: 'Bedürftige Gemeinschaften unterstützen.',
    impactCard1Title: 'Unser Engagement',
    impactCard1Body:
      'Bei MahMedix glauben wir, dass der Zugang zu qualitativ hochwertiger Gesundheitsversorgung und pharmazeutischer Innovation kein Privileg sein sollte – es sollte ein Recht sein. Über unsere kommerziellen Projekte hinaus engagieren wir uns dafür, unsere Expertise zu nutzen, um gefährdete Gemeinschaften zu unterstützen.',
    impactCard2Title: 'Unsere Initiativen',
    impactCard2Body:
      'Wir unterstützen Programme, die sich mit Kinderhunger und Unterernährung befassen. Durch Zusammenarbeit mit lokalen Organisationen tragen wir zu Initiativen bei, die Kindern in Not nahrhafte Lebensmittel, Gesundheitsvorsorge, Impf- und Arzneimittelproduktion sowie Bildung bereitstellen.',
    impactCard3Title: 'Unsere Vision',
    impactCard3Body:
      'Jedes erfolgreiche Projekt bringt uns unserem Ziel näher: Medizin und Pharma voranzutreiben – für alle, überall.',

    ctaTitle: 'Bereit, Ihr nächstes Pharma-, Medizin- oder Gesundheitsprojekt auf das nächste Level zu heben?',
    ctaBody:
      'Lassen Sie uns besprechen, wie MahMedix Sie bei der Konzeption, Implementierung und Evaluierung Ihrer Initiativen unterstützen kann.',
    ctaBtn: 'Unser Team kontaktieren',
  },

  about: {
    pageLabel: 'Über uns',
    heroHeadline:
      'Bei MahMedix verwandeln wir komplexe pharmazeutische, medizinische und gesundheitliche Herausforderungen in',
    heroHeadlineAccent: 'erfolgreiche Projekte.',
    heroSub1: 'Ihr vertrauenswürdiger Partner vom Konzept bis zur klinischen Anwendung.',
    heroSub2:
      'MahMedix vereint ein Team hocherfahrener Fachleute, das tiefes Branchenwissen in Pharmazie, Medizin, Gesundheitswesen und künstlicher Intelligenz kombiniert, um außergewöhnliche Ergebnisse zu liefern.',

    storyLabel: 'Unsere Geschichte & Mission',
    storyTitle: 'Innovation und Anwendung',
    storyTitleAccent: 'verbinden',
    storyBody1:
      'MahMedix wurde mit einer klaren Vision gegründet: die pharmazeutische, medizinische und Gesundheitsbranche mit intelligenten, ergebnisorientierten Lösungen zu unterstützen. Wir glauben, dass erfolgreiche Projekte mehr als nur technisches Know-how erfordern – sie erfordern ein tiefes Verständnis sowohl wissenschaftlicher Prinzipien als auch realer klinischer Bedürfnisse.',
    storyBody2:
      'Heute arbeiten wir an der Schnittstelle von Pharmawissenschaft, Medizintechnologie und datengesteuerter Innovation. Unsere Mission ist es, unseren Partnern zu helfen, Komplexität zu bewältigen, Risiken zu reduzieren und bedeutungsvolle Ergebnisse zu erzielen.',
    storyBody3:
      'Darüber hinaus entwickeln wir eigene innovative Projekte und bereiten diese für den Markt vor.',
    missionTitle: 'Unsere Mission',
    missionBody:
      'Partnern helfen, Komplexität zu bewältigen, Risiken zu reduzieren und bedeutungsvolle Ergebnisse im pharmazeutischen, medizinischen und gesundheitlichen Bereich zu erzielen.',
    visionTitle: 'Unsere Vision',
    visionBody:
      'Der vertrauenswürdigste Innovationspartner in der pharmazeutischen und gesundheitlichen Projektentwicklung zu sein – weltweit.',

    approachLabel: 'Unser Ansatz',
    approachTitle: 'Wie wir arbeiten',
    approachSub:
      'Wir glauben nicht an Einheitslösungen. Jedes Projekt ist einzigartig, und wir passen unseren Ansatz entsprechend an.',
    approaches: [
      { icon: '🤝', title: 'Kollaborative Partnerschaft', desc: 'Wir arbeiten eng mit Ihrem Team zusammen, um Ihre spezifischen Ziele, Herausforderungen und Rahmenbedingungen zu verstehen.' },
      { icon: '🔭', title: 'Wissenschaftliche Strenge', desc: 'Unsere Arbeit basiert auf soliden pharmazeutischen und medizinischen Prinzipien und stellt sicher, dass jede Empfehlung sowohl innovativ als auch praktisch ist.' },
      { icon: '🔄', title: 'Lückenlose Unterstützung', desc: 'Vom ersten Konzept und Design über die Implementierung bis zur klinischen Anwendung sind wir in jeder Phase an Ihrer Seite.' },
      { icon: '💡', title: 'Datengestützte Erkenntnisse', desc: 'Wir nutzen fortschrittliche Berechnungstools, um Erkenntnisse zu gewinnen, Prozesse zu optimieren und Entscheidungen zu unterstützen – stets mit Fokus auf den Projekterfolg.' },
    ],

    expertiseLabel: 'Unsere Expertise',
    expertiseTitle: 'Was wir in Ihr',
    expertiseTitleAccent: 'Projekt einbringen',
    expertiseSub:
      'Unser Team kombiniert tiefes Fachwissen mit praktischer Erfahrung im pharmazeutischen, medizinischen und gesundheitlichen Bereich.',
    expertise: [
      { icon: '💊', title: 'Pharmazeutische Entwicklung', desc: 'Arzneimittelabgabesysteme, Formulierungsoptimierung und API-Herstellung.' },
      { icon: '🏭', title: 'Anlagenplanung & Implementierung', desc: 'Entwurf und Ausführung von Produktionsanlagen für verschiedene Darreichungsformen.' },
      { icon: '🩺', title: 'Intelligente Medizin- & Klinische Geräte', desc: 'Entwicklung und Integration intelligenter Technologien für Diagnostik, Überwachung und Behandlung.' },
      { icon: '📋', title: 'Klinische Strategie & Forschung', desc: 'Programmbewertung, Risikomanagement, CRO-Unterstützung und Optimierung klinischer Studien.' },
      { icon: '🧬', title: 'Biomedizinische & Bioinformatik-Analyse', desc: 'Fortschrittliche Analyse medizinischer und biologischer Daten zur Unterstützung von Forschung und Entwicklung.' },
      { icon: '📈', title: 'Geschäfts- & Marktstrategie', desc: 'Gewinnschätzung, Arzneimittelkorbvorbereitung und Marktzugangsplanung.' },
    ],

    gmedixLabel: 'Aktuelles Projekt',
    gmedixTitle: 'G-Medix',
    gmedixBody1:
      'G-Medix ist ein hochinnovatives Projekt zur Entwicklung einer neuartigen Methode und eines Geräts für die Gefäßgesundheit. Konzipiert zur Lösung kritischer Bedürfnisse in Diagnostik, Überwachung und Intervention – G-Medix kombiniert fortschrittliche biomedizinische Technik mit intelligenter Datenverarbeitung für präzise Gefäßversorgung.',
    gmedixBody2:
      'Das Projekt wird für mehrere Anwendungen im gesamten Gesundheitsspektrum entwickelt. In der ersten Phase wird G-Medix Krankenhäuser, Kliniken und Gesundheitsprogramme bedienen – Fachleuten ein leistungsstarkes Tool für Gefäßbeurteilung und Behandlungsplanung bereitstellen.',
    gmedixBody3:
      'Durch die Überbrückung der Lücke zwischen klinischer Innovation und persönlicher Gesundheitstechnologie zielt G-Medix darauf ab, die Früherkennung zu verbessern, Patientenergebnisse zu verbessern und Einzelpersonen zu befähigen, ihre Gefäßgesundheit zu kontrollieren.',
    gmedixPhasesTitle: 'Entwicklungsphasen',
    gmedixPhase1Label: 'Phase 1',
    gmedixPhase1Title: 'Klinisch & Professionell',
    gmedixPhase1Body:
      'Krankenhäuser, Kliniken und Gesundheitsprogramme mit präzisen Werkzeugen für Gefäßbeurteilung und Behandlungsplanung.',
    gmedixPhase2Label: 'Phase 2',
    gmedixPhase2Title: 'Persönliche Gesundheit',
    gmedixPhase2Body:
      'Medizingerät und Begleit-App für das persönliche Gefäßgesundheitsmanagement außerhalb klinischer Einrichtungen.',

    impactLabel: 'Gesellschaftliche Wirkung',
    impactTitle: 'Gesundheitsgerechtigkeit fördern.',
    impactTitleAccent: 'Bedürftige Gemeinschaften unterstützen.',
    impactCommitmentTitle: 'Unser Engagement',
    impactCommitmentBody:
      'Bei MahMedix glauben wir, dass der Zugang zu qualitativ hochwertiger Gesundheitsversorgung kein Privileg sein sollte. Über unsere kommerziellen Projekte hinaus engagieren wir uns dafür, gefährdete Gemeinschaften zu unterstützen und globale Gesundheitsherausforderungen anzugehen.',
    impactInitTitle: 'Unsere Initiativen',
    impactInitBody:
      'Wir unterstützen Programme gegen Kinderhunger und Unterernährung. Durch Zusammenarbeit mit lokalen Organisationen und Unternehmen tragen wir zu Initiativen bei, die Kindern in Not nahrhafte Lebensmittel, Gesundheitsvorsorge, Impfstoff- und Arzneimittelproduktion sowie Bildung bereitstellen.',
    impactPartnerTitle: 'Partner werden',
    impactPartnerBody:
      'Wenn Sie eine Organisation im Bereich globale Gesundheit, humanitäre Hilfe oder Gemeinschaftsentwicklung vertreten, freuen wir uns von Ihnen zu hören.',
    impactQuote:
      'Jedes erfolgreiche Projekt bringt uns unserem Ziel näher: Medizin und Pharma voranzutreiben – für alle, überall.',

    ctaTitle: 'Bereit, mit MahMedix zusammenzuarbeiten?',
    ctaBody:
      'Lassen Sie uns besprechen, wie wir Ihre nächste pharmazeutische, medizinische oder gesundheitliche Initiative unterstützen können.',
    ctaBtn: 'Unser Team kontaktieren',
  },

  contact: {
    pageLabel: 'Kontakt',
    heroTitle: 'Lassen Sie uns verbinden.',
    heroSub: 'Haben Sie ein Projekt im Sinn oder möchten mehr über unsere Innovationen erfahren? Wir helfen Ihnen gerne.',

    infoTitle: 'Kontaktinformationen',
    infoGeneral: 'Allgemeine Anfragen',
    infoPhone: 'Telefon',
    infoAddress: 'Büroadresse',
    infoAddressLines: ['Ernst-Thälmann Ring 64a', '17491 Greifswald', 'Deutschland'],
    infoHoursTitle: 'Bürozeiten',
    infoHoursValue: 'Montag – Freitag: 9:00 – 18:00 Uhr',
    infoImpactTitle: 'Anfragen zur gesellschaftlichen Wirkung',

    formTitle: 'Kontakt aufnehmen',
    formName: 'Name',
    formNamePlaceholder: 'Ihr vollständiger Name',
    formEmail: 'E-Mail',
    formEmailPlaceholder: 'ihre@email.de',
    formPhone: 'Telefon',
    formPhonePlaceholder: '+49 xxx xxxxxxxx',
    formSubject: 'Betreff',
    formSubjectPlaceholder: 'Wie können wir helfen?',
    formMessage: 'Nachricht',
    formMessagePlaceholder: 'Erzählen Sie uns von Ihrem Projekt oder Ihrer Anfrage…',
    formSend: 'Nachricht senden',
    formSending: 'Wird gesendet…',

    errName: 'Name ist erforderlich.',
    errEmailRequired: 'E-Mail ist erforderlich.',
    errEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    errSubject: 'Betreff ist erforderlich.',
    errMessage: 'Nachricht ist erforderlich.',

    successTitle: 'Nachricht gesendet!',
    successBody: 'Vielen Dank für Ihre Nachricht. Wir werden uns innerhalb eines Werktages bei Ihnen melden.',
    successBtn: 'Weitere Nachricht senden',

    mapTitle: 'Uns finden',
  },

  impressum: {
    pageLabel: 'Rechtliches',
    heroTitle: 'Impressum',
    contactInfoTitle: 'Kontaktinformationen',
    registerTitle: 'Registerinformationen',
    register: 'Register',
    municipality: 'Gemeindeschlüssel',
    responsibleTitle: 'Verantwortlich für den Inhalt gemäß § 55 Abs. 2 RStV',
    responsibleBody: 'MahMedix',
    liabilityContentTitle: 'Haftung für Inhalte',
    liabilityContentBody:
      'Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.',
    liabilityLinksTitle: 'Haftung für Links',
    liabilityLinksBody:
      'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.',
    copyrightTitle: 'Urheberrecht',
    copyrightBody:
      'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.',
  },

  ui: {
    lightMode: 'Hell',
    darkMode: 'Dunkel',
    systemMode: 'System',
    theme: 'Design',
    language: 'Sprache',
  },
};

export const translations: Record<Language, Translations> = { en, de };
